import type { DragEvent } from 'react';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import ElementInfoAdapter from '@src/slide/application/service/edit-action-services/element-info';
import type { IDragLogicCallback } from './drag-logic.interface';
import { IRelativeProps } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import { isCtrlKeyDown } from '@src/utils/key-mouse-event/ctrl-key';
import { IPosition  } from './base-logic/base-drag-logic';
import { PCBaseDragLogic } from './base-logic/pc-base-drag-logic';
import { MobileBaseDragLogic } from './base-logic/mobile-drag-logic';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

interface IMouseDownProps {
    event: DragEvent,
    scale: number,
    isDraggable: boolean,
    relativeProps: IRelativeProps,
    /**
     * 无需传递翻转信息，避免在未选中组合子元素的情况下，点击子元素拖拽组合时，组合应用子元素的翻转信息导致翻转数据出错
     */
    size: IRect,
    sizeOnSlide: IRect,
    isEditing?: boolean,
    afterDragClear?: () => void;
}

/**
 * 通用拖拽逻辑
 */
export default class DragLogic {
    /**
     * 拖拽开始鼠标坐标
     */
    private startMousePosition: any;

    /**
     * 拖拽开始时候的model尺寸等信息
     */
    private sizeAtStart: IRect | null;

    /**
     * 拖拽执行的回调
     */
    private delegate: IDragLogicCallback | null;

    /**
     * 当前页面缩放
     */
    private scale: number;

    /**
     * 相对Props
     */
    private relativeProps: IRelativeProps | null;

    private isCtrlDown: boolean;

    private pcDragLogic: PCBaseDragLogic;
    private mobileDragLogic: MobileBaseDragLogic;
    private size: IRect;
    private sizeOnSlide: IRect;

    public constructor(delegate: IDragLogicCallback | null) {
        this.delegate = delegate;
        this.pcDragLogic = new PCBaseDragLogic();
        this.mobileDragLogic = new MobileBaseDragLogic();
    }

    /**
     * 点击到元素上
     * @param event
     * @param element
     * @param scale
     */
    public handleMouseDownOnElement = (props: IMouseDownProps): void => {
        if (!props.isDraggable) return;
        if (props.event?.nativeEvent.which === 1) {
            this.scale = props.scale;
            this.relativeProps = props.relativeProps;
            this.size = props.size;
            this.sizeOnSlide = props.sizeOnSlide;
            this.pcDragLogic.handleMouseDownOnElement({
                event: props.event,
                dragStart: this.startDrag,
                onDrag: this.handleMove,
                dragEnd: (e) => {
                    this.stopDrag(e);
                    props.afterDragClear?.();
                },
            });
        }
    };

    /**
     * touch到元素上
     * @param event
     * @param element
     * @param scale
     */
    public handleTouchStartOnElement = (props: IMouseDownProps): void => {
        if (!props.isDraggable) return;
        this.scale = props.scale;
        this.relativeProps = props.relativeProps;
        this.size = props.size;
        this.sizeOnSlide = props.sizeOnSlide;
        this.mobileDragLogic.handleTouchStartOnElement({
            event: props.event,
            isEditing: props.isEditing,
            dragStart: this.startDrag,
            onDrag: this.handleMove,
            dragEnd: (e) => {
                this.stopDrag(e);
                props.afterDragClear?.();
            },
        });
    };

    /**
     * 开始拖拽
     * @param event
     * @param element
     * @param scale
     */
    private startDrag = (event: MouseEvent, startMousePosition: IPosition) => {
        this.startMousePosition = startMousePosition;
        this.isCtrlDown = isCtrlKeyDown(event);
        this.sizeAtStart = this.isCtrlDown ? this.sizeOnSlide : this.size;
        this.delegate?.onDragStartOnElement(this.sizeAtStart, event);
    };

    /**
     * 拖拽结束
     * @param event
     */
    private stopDrag = (event: MouseEvent) => {
        this.delegate?.onDragStopOnElement(MODES.MOVE, event);
        if (this.startMousePosition) {
            this.startMousePosition = null;
        }
        if (this.sizeAtStart) {
            this.sizeAtStart = null;
        }
    };

    /**
     * 鼠标移动处理
     * @param event
     */
    private handleMove = (event: MouseEvent) => {
        const coords = this.getEventCoordinates(event);
        const targetSize = this.targetSizeOnMove(coords, event);
        // @ts-ignore
        this.delegate?.onDragOnElement(event, this.sizeAtStart, targetSize, MODES.MOVE);
    };

    private targetSizeOnMove(coords: { x: any; y: any }, event: MouseEvent | TouchEvent) {
        const targetSize = { ...this.sizeAtStart };
        const windowZoom = 1;
        let offsetX = (coords.x - this.startMousePosition.x) / this.scale / windowZoom;
        let offsetY = (coords.y - this.startMousePosition.y) / this.scale / windowZoom;
        if (event.shiftKey) {
            if (Math.abs(offsetX) > Math.abs(offsetY)) {
                offsetY = 0;
            } else {
                offsetX = 0;
            }
        }
        // 当按下ctrl的同时drag，copy的元素均相对slide
        if (!this.isCtrlDown) {
            const offset = ElementInfoAdapter
                .getOffsetWithRelative(this.relativeProps, offsetX, offsetY);
            ({ offsetX, offsetY } = offset);
        }
        if (!targetSize.left) {
            targetSize.left = 0;
        }
        if (!targetSize.top) {
            targetSize.top = 0;
        }
        targetSize.left += offsetX;
        targetSize.top += offsetY;
        return targetSize;
    }

    /**
     * 获取事件坐标
     * @param e
     */
    private getEventCoordinates = (e: any): IPosition => {
        const event = e.nativeEvent || e;
        return {
            x: event?.touches?.[0] ? event.touches[0].clientX : event.clientX,
            y: event?.touches?.[0] ? event.touches[0].clientY : event.clientY,
        };
    };
}
